import { ComponentResource } from '@/api/serverApi'
import { TBoltFormInputs, tboltSchema, TriggerUpdateKey } from '../../properties/TboltProperties'
import { AppDispatch } from '@/stores'
import { useDispatch } from 'react-redux'
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react'
import { useForm, UseFormGetValues, UseFormRegister } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import * as v from 'valibot'
import { deleteComponentAction, setSelectedComponent, updateComponentAction } from '@/stores/active-design'
import PanelRangeInput from '../../properties/components/PanelRangeInput'
import { LayeredPlusIcon, SubtractIcon, TextInput } from '@cfs-fixings/shared'
import PanelInput from '../../properties/components/PanelInput'
import ConfirmationModal from '@/components/modals/ConfirmationModal'
import _ from 'lodash'
import AddProductPicker from '../../product-picker/AddProductPicker'
import { LoadsContext } from '../hooks/LoadsContext'
import { LoadTypes, safetyFactors } from './LoadTypeForm'
import ComponentType from '@/enums/ComponentType'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'

const LoadForms = () => {
  const { design } = useContext(LoadsContext)
  const [isProductPickerOpen, setIsProductPickerOpen] = useState(false)

  const loads = design?.components.filter((component) => {
    const hasParent = component?.parent_id
    const isTBolt = Object.hasOwnProperty.call(component, 't_bolt')
    return hasParent && isTBolt
  })

  const channelComponent = useMemo(() => {
    return design?.components.find(component =>
      component.type === ComponentType.ProductVariant
      && (component.cast_in_anchor_channel?.length !== null || component.cast_in_anchor_channel?.custom_length !== null),
    )
  }, [design?.components])

  const channelLength = useMemo(() => {
    return channelComponent?.cast_in_anchor_channel?.length || channelComponent?.cast_in_anchor_channel?.custom_length || 3050
  }, [channelComponent])

  return (
    <>
      <div className="flex flex-col">
        {
          loads && loads.map((tbolt, index) => {
            return (
              <LoadForm
                key={`tbolt-load-des-${index}`}
                load={tbolt}
                number={index + 1}
                onActionClick={() => setIsProductPickerOpen(true)}
                channelLength={channelLength}
              />
            )
          })
        }
      </div>
      <AddProductPicker
        isOpen={isProductPickerOpen}
        onClose={() => setIsProductPickerOpen(false)}
      />
    </>
  )
}

// ---

type LoadFormProps = {
  load: ComponentResource
  number: number
  onActionClick: () => void
  channelLength: number
}

const LoadForm = ({ load, number, onActionClick, channelLength }: LoadFormProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const [errorKeys, setErrorKeys] = useState<string[]>([])
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const { loadType } = useContext(LoadsContext)

  const defaultValues = useMemo(() => {
    return {
      t_bolt: {
        shear_force: load.t_bolt?.shear_force ? parseFloat(load.t_bolt?.shear_force) : 0,
        tension_force: load.t_bolt?.tension_force ? parseFloat(load.t_bolt?.tension_force) : 0,
        stand_off_distance: load.t_bolt?.stand_off_distance ? parseFloat(load.t_bolt?.stand_off_distance) : 0,
      },
      position: {
        x: load.position?.x,
      },
    }
  }, [load])

  const { register, watch, setValue, reset } = useForm<DesignLoadFormInputs>({
    resolver: valibotResolver(designLoadSchema),
    defaultValues: {
      ...defaultValues,
    },
  })

  const { register: characteristicRegister, getValues: getCharacteristicValues, reset: resetCharacteristic } = useForm<CharacteristicLoadFormInputs>({
    resolver: valibotResolver(characteristicLoadSchema),
    defaultValues: {
      shear_dead: 0,
      shear_live: 0,
      tension_dead: 0,
      tension_live: 0,
    },
  })

  /**
   * We have this useEffect here to reset the form when details of a TBolt in a group are changed,
   * as the details of other TBolts in the group will be updated to match
   */
  useEffect(() => {
    if (load.group_id) {
      const changesMade = !_.isEqual(defaultValues, watch())
      if (changesMade) {
        reset(defaultValues)
      }
    }
  }, [load, defaultValues, reset, watch])

  // Copied from TboltProperties.tsx
  const updatePosition = (key: 'x' | 'y', value: string) => {
    let parsedValue = value.trim() as string | number
    if (parsedValue !== '') {
      parsedValue = parseFloat(value)
    }

    const vResult = v.safeParse(tboltSchema, { position: { [key]: parsedValue } })

    if (!vResult.success) {
      setErrorKeys([
        ...errorKeys,
        ...vResult.issues.map(i => (
          i.path!.reduce((acc, curr) => [...acc, curr.key as string], [] as string[]).join('.')
        )),
      ])
      return
    }

    setErrorKeys(errorKeys.filter(k => k !== `position.${key}`))

    dispatch(updateComponentAction({
      componentId: load.id!,
      details: {
        position: {
          [key]: parsedValue,
        },
      },
    }))
  }

  // Copied from TboltProperties.tsx
  const triggerUpdate = async (key: TriggerUpdateKey, value: any, from: LoadTypes = LoadTypes.Design) => {
    const vResult = v.safeParse(tboltSchema, { [key]: value })

    if (!vResult.success) {
      setErrorKeys([
        ...errorKeys,
        ...vResult.issues.map(i => (
          i.path!.reduce((acc, curr) => [...acc, curr.key as string], [] as string[]).join('.')
        )),
      ])
      return
    }

    setErrorKeys(errorKeys.filter(k => k !== key))

    dispatch(updateComponentAction({
      componentId: load.id,
      details: {
        t_bolt: {
          [key]: value,
        },
      },
    }))

    /**
     * When engineers make a change from the design form, clear the characteristic form
     * so engineers can calculate the characteristic forces again
     */
    if (from === LoadTypes.Design) {
      resetCharacteristic({
        shear_dead: 0,
        shear_live: 0,
        tension_dead: 0,
        tension_live: 0,
      })
    }

    /**
     * When engineers make a change from the characteristic form, update the design form
     * inputs respectively
     */
    if (from === LoadTypes.Characteristic) {
      const tBoltKey = 't_bolt.' + key as keyof DesignLoadFormInputs
      setValue(tBoltKey, value)
    }
  }

  const onInputFocus = () => {
    dispatch(setSelectedComponent(load.id))
  }

  const textSpec = {
    loadTitle: 'Load :number',
    input: {
      boltPosition: 'Bolt Position',
    },
    removeModal: {
      title: 'Remove Load',
      message: 'Are you sure you want to remove this load?',
    },
  }

  const onRangeInputCHange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.trim()

    if (value === '') {
      return setValue('position.x', 0)
    }

    const parsedValue = parseFloat(value)

    if (parsedValue > channelLength) {
      toastHelper.dismissibleError('Invalid Position', `Position must be less than or equal to ${channelLength}`)
      return setValue('position.x', channelLength)
    }

    return setValue('position.x', parsedValue)
  }

  return (
    <>
      <div
        onFocus={onInputFocus}
        className="border-b-[1px] border-b-gray-400"
      >
        <div className="p-4">
          <div className="flex items-center justify-between gap-x-4">
            <h3 className="font-semibold text-xs leading-[14px] text-gray-900">
              {textSpec.loadTitle.replace(':number', number.toString())}
            </h3>
            <div className="flex items-center gap-x-1">
              <button
                onClick={onActionClick}
                className="focus:outline-black"
              >
                <LayeredPlusIcon className="size-4" />
              </button>
              <button
                onClick={() => setDeleteConfirmation(true)}
                className="focus:outline-black"
              >
                <SubtractIcon className="size-4" />
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-y-4 mt-2">
            {
              loadType === LoadTypes.Design && (
                <DesignForm
                  register={register}
                  onBlur={triggerUpdate}
                  errorKeys={errorKeys}
                />
              )
            }
            {
              loadType === LoadTypes.Characteristic && (
                <CharacteristicForm
                  getValues={getCharacteristicValues}
                  register={characteristicRegister}
                  onBlur={triggerUpdate}
                />
              )
            }
            <div>
              <h3 className="font-semibold text-[14px] text-gray-900 mb-2">
                {textSpec.input.boltPosition}
              </h3>
              <div className="flex w-full gap-x-4 justify-between">
                <div className="max-w-[105px] w-full">
                  <PanelRangeInput
                    {...register('position.x')}
                    onMouseUp={(e) => updatePosition('x', e.currentTarget.value)}
                    max={channelLength}
                    min={0}
                  />
                </div>
                <div className="w-full max-w-[100px]">
                  <TextInput
                    variant="xs"
                    title={textSpec.input.boltPosition}
                    value={watch('position.x')}
                    onBlur={e => updatePosition('x', e.target.value)}
                    onChange={onRangeInputCHange}
                    defaultValue={load.position?.x.toString()}
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <ConfirmationModal
        title={textSpec.removeModal.title}
        message={textSpec.removeModal.message}
        isOpen={deleteConfirmation}
        onClose={() => setDeleteConfirmation(false)}
        onConfirm={() => {
          dispatch(deleteComponentAction(load.id))
          setDeleteConfirmation(false)
        }}
      />
    </>
  )
}

export default LoadForms

// --- Form Sections
const designLoadSchema = tboltSchema
type DesignLoadFormInputs = TBoltFormInputs

type DesignFormProps = {
  register: UseFormRegister<DesignLoadFormInputs>
  onBlur: (key: TriggerUpdateKey, value: any) => void
  errorKeys: string[]
}

const DesignForm = ({ register, onBlur, errorKeys }: DesignFormProps) => {
  const textSpec = {
    input: {
      design: {
        shearForce: 'V[small]Edy[/small](kN-m)',
        tensionForce: 'N[small]Ed[/small](kN)',
      },
      standOffDistance: 'Stand-off distance',
    },
  }

  return (
    <div className="flex flex-col gap-y-4">
      <PanelInput
        title={textSpec.input.design.shearForce}
        {...register('t_bolt.shear_force')}
        onBlur={e => onBlur('shear_force', parseFloat(e.target.value))}
        error={errorKeys.includes('shear_force')}
      />
      <PanelInput
        title={textSpec.input.design.tensionForce}
        {...register('t_bolt.tension_force')}
        onBlur={e => onBlur('tension_force', parseFloat(e.target.value))}
        error={errorKeys.includes('tension_force')}
      />
      <PanelInput
        title={textSpec.input.standOffDistance}
        {...register('t_bolt.stand_off_distance')}
        onBlur={e => onBlur('stand_off_distance', parseFloat(e.target.value))}
        error={errorKeys.includes('stand_off_distance')}
      />
    </div>
  )
}

// ---

const characteristicLoadSchema = v.object({
  shear_live: v.number([v.minValue(0)]),
  shear_dead: v.number([v.minValue(0)]),
  tension_live: v.number([v.minValue(0)]),
  tension_dead: v.number([v.minValue(0)]),
})
type CharacteristicLoadFormInputs = v.Input<typeof characteristicLoadSchema>

type CharacteristicFormProps = {
  getValues: UseFormGetValues<CharacteristicLoadFormInputs>
  register: UseFormRegister<CharacteristicLoadFormInputs>
  onBlur: (key: TriggerUpdateKey, value: any, from: LoadTypes) => void
}

const CharacteristicForm = ({ getValues, register, onBlur }: CharacteristicFormProps) => {

  const calculateCharacteristicForce = (type: 'shear' | 'tension') => {
    const dead = getValues(`${type}_dead`)
    const live = getValues(`${type}_live`)

    const calculatedDead = dead * safetyFactors.permanent
    const calculatedLive = live * safetyFactors.variableLoad

    const calculatedForce = calculatedDead + calculatedLive

    return onBlur(`${type}_force`, calculatedForce, LoadTypes.Characteristic)
  }

  const textSpec = {
    input: {
      characteristic: {
        shearDead: 'N[small]Gk[/small]',
        shearLive: 'N[small]Qk[/small]',
        tensionDead: 'V[small]Gk[/small]',
        tensionLive: 'V[small]Qk[/small]',
      },
    },
  }

  return (
    <div className="flex gap-x-4">
      <PanelInput
        title={textSpec.input.characteristic.shearDead}
        inputSize="sm"
        titleDirection="col"
        {...register('shear_dead')}
        onBlur={() => calculateCharacteristicForce('shear')}
      />
      <PanelInput
        title={textSpec.input.characteristic.shearLive}
        inputSize="sm"
        titleDirection="col"
        {...register('shear_live')}
        onBlur={() => calculateCharacteristicForce('shear')}
      />
      <PanelInput
        title={textSpec.input.characteristic.tensionDead}
        inputSize="sm"
        titleDirection="col"
        {...register('tension_dead')}
        onBlur={() => calculateCharacteristicForce('tension')}
      />
      <PanelInput
        title={textSpec.input.characteristic.tensionLive}
        inputSize="sm"
        titleDirection="col"
        {...register('tension_live')}
        onBlur={() => calculateCharacteristicForce('tension')}
      />
    </div>
  )
}
